import { useCallback, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { PortalApiError, portalApi } from "@/lib/portal-api"
import type { PortalVerifyResult } from "@/lib/database.types"
import { track } from "@/lib/posthog"
import { supabase } from "@/lib/supabase"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"

function getParamFromUrl(key: string): string | null {
  const searchParams = new URLSearchParams(window.location.search)
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""))
  return searchParams.get(key) || hashParams.get(key)
}

type VerificationResult =
  | { success: true; nextPath: string }
  | { success: false; message: string; retryAfterSeconds?: number }

async function requestPortalVerification(): Promise<VerificationResult> {
  try {
    let accessToken = getParamFromUrl("access_token")
    const portalToken = getParamFromUrl("portal_token")
    const tokenHash = getParamFromUrl("token_hash")
    const type = getParamFromUrl("type")
    const errorDescription = getParamFromUrl("error_description")

    if (errorDescription) {
      return { success: false, message: decodeURIComponent(errorDescription) }
    }

    if (!portalToken) {
      return {
        success: false,
        message: "Missing verification details. Please request a new link.",
      }
    }

    if (!accessToken && tokenHash && type) {
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type as "signup" | "email" | "recovery" | "magiclink" | "email_change" | "invite",
      })

      if (error) {
        throw error
      }

      accessToken = data.session?.access_token || null
    }

    if (!accessToken) {
      return {
        success: false,
        message: "Missing verification details. Please request a new link.",
      }
    }

    const result = await portalApi<PortalVerifyResult>("/api/portal/verify", {
      method: "POST",
      body: JSON.stringify({ access_token: accessToken, portal_token: portalToken }),
    })

    return { success: true, nextPath: result.requires_selection ? "/select" : "/dashboard" }
  } catch (err) {
    if (err instanceof PortalApiError && err.statusCode === 429) {
      const retryAfterSeconds = err.retryAfterSeconds || 60
      return {
        success: false,
        message: `Too many verification attempts. Try again in ${retryAfterSeconds} seconds.`,
        retryAfterSeconds,
      }
    }

    return {
      success: false,
      message: err instanceof Error ? err.message : "Verification failed",
    }
  }
}

export function PortalVerify() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null)
  const [now, setNow] = useState(0)

  useEffect(() => {
    if (!rateLimitedUntil) return

    const interval = window.setInterval(() => {
      const currentTime = Date.now()
      setNow(currentTime)
      if (currentTime >= rateLimitedUntil) {
        setRateLimitedUntil(null)
      }
    }, 1000)

    return () => window.clearInterval(interval)
  }, [rateLimitedUntil])

  const remainingSeconds = rateLimitedUntil
    ? Math.max(Math.ceil((rateLimitedUntil - now) / 1000), 0)
    : 0

  const applyVerificationResult = useCallback(
    (result: VerificationResult) => {
      if (!result.success) {
        if (result.retryAfterSeconds) {
          const requestTime = Date.now()
          setNow(requestTime)
          setRateLimitedUntil(requestTime + result.retryAfterSeconds * 1000)
        }
        setError(result.message)
        setLoading(false)
        return
      }

      setLoading(false)
      track("portal_login")
      // Remove one-time credentials while retaining a safe history entry for fresh magic-link tabs.
      window.history.replaceState(window.history.state, "", "/")
      navigate(result.nextPath)
    },
    [navigate]
  )

  const verify = useCallback(() => {
    setLoading(true)
    setError(null)
    void requestPortalVerification().then(applyVerificationResult)
  }, [applyVerificationResult])

  useEffect(() => {
    let cancelled = false

    void requestPortalVerification().then((result) => {
      if (!cancelled) {
        applyVerificationResult(result)
      }
    })

    return () => {
      cancelled = true
    }
  }, [applyVerificationResult])

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Verifying Access</CardTitle>
          <CardDescription>Please wait while we sign you in.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <>
              <p className="text-destructive text-sm">
                {error}
                {remainingSeconds > 0 ? ` ${remainingSeconds}s remaining.` : ""}
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="outline"
                  onClick={verify}
                  disabled={loading || remainingSeconds > 0}
                >
                  {remainingSeconds > 0 ? `Retry In ${remainingSeconds}s` : "Try Again"}
                </Button>
                <Button asChild variant="ghost">
                  <Link to="/">Back to portal login</Link>
                </Button>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground text-sm">
              {loading ? "This usually takes a few seconds." : "Waiting to continue."}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
